import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IPaginated, IResource } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import { afterReceiving, afterReceivingCommentResponse, afterReceivingNoteResponse, afterReceivingRecruitmentProcesses, beforeSending, beforeSendingComment, beforeSendingNote, beforeSendingCandidateAndProcess} from '@/services/recruitment/hooks'
import { afterReceiving as afterReceivingResource } from '@/services/resource/hooks'

const resource = 'recruitmentProcess'

const getByMeeting = (axios: IFactoryFunctionRequest<any, any>) => (meetingId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/byMeeting/${meetingId}`,
  })
}

const screeningList = (axios: IFactoryFunctionRequest<IPaginated<IResource>, any >) => (query: { page?: number; limit?: number; display?: string; processId?: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/inbox/paginated`,
    query,
    afterReceivingHook:afterReceiving
  })
}

const getProcessEvaluation = (axios: IFactoryFunctionRequest<IResource, any >) => ({ processId }: { processId: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/${processId}`,
    afterReceivingHook:afterReceiving
  })
}

const getProcessEditCandidateInformations = (axios: IFactoryFunctionRequest<IResource, any >) => (processId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${processId}/editCandidateInformations`,
    afterReceivingHook: afterReceiving
  })
}

const getProcessCandidateInformation = (axios: IFactoryFunctionRequest<IResource, any >) => (processId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${processId}/candidateInformation`,
    afterReceivingHook: afterReceiving
  })
}

const updateRecruitmentProcess = (axios: IFactoryFunctionRequest<IResource, any >) => (process: unknown) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${process._id}`,
    data: process,
    beforeSendingHook:beforeSending,
    afterReceivingHook:afterReceiving
  })
}

const updateManyStatuses = (axios: IFactoryFunctionRequest<IResource, any >) => (processes: unknown[]) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/updateManyStatuses`,
    data: { processes },
    beforeSendingHook:beforeSending,
  })
}

const partialUpdateRecruitmentProcess = (axios: IFactoryFunctionRequest<IResource, any >) => (processId: string, process: unknown) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${processId}`,
    data: process,
    beforeSendingHook:beforeSending,
    afterReceivingHook:afterReceiving
  })
}

const candidateBaseList = (axios: IFactoryFunctionRequest<IPaginated<IResource>, any >) => (query: { page?: number; limit?: number; search?: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/getDatabaseProcesses`,
    query,
    afterReceivingHook:afterReceiving
  })
}
const getProcessList = (axios: IFactoryFunctionRequest<IResource, any >) => (query) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/kanban`,
    query,
    afterReceivingHook:afterReceiving
  })
}
const getProcessInterviewPage = (axios: IFactoryFunctionRequest<IResource, any >) => (processId) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/interviews/${processId}`,
    afterReceivingHook:afterReceiving
  })
}
const addDocumentToProcess = (axios: IFactoryFunctionRequest<any, any>) => (processId: string, documents: Array<Partial<DocumentType>>) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${processId}/documents`,
      data: { documents },
    })
}

const deleteDocumentFromProcess = (axios: IFactoryFunctionRequest<any, any>) => (processId, documentId) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${processId}/documents/${documentId}`,
  })
}

const toggleDocumentPublicStatus = (axios: IFactoryFunctionRequest<any, any>) => (processId, documentId, isPublic) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${processId}/documents/${documentId}/togglePublicStatus`,
    data: { isPublic },
  })
}

const moveDocumentInProcess = (axios: IFactoryFunctionRequest<any, any>) => (processId, fromIndex, toIndex) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${processId}/documents/move`,
    data: { fromIndex, toIndex },
  })
}



const addCommentToProcess = (axios: IFactoryFunctionRequest<any, any>) => (processId, comment: any) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/addComment/${processId}`,
    data: comment,
    beforeSendingHook: beforeSendingComment,
    afterReceivingHook: afterReceivingCommentResponse
  })
}

const updateCommentToProcess = (axios: IFactoryFunctionRequest<any, any>) => (processId: string, commentId: string, comment: {content: string, taggedPopulated: any[]}) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/updateComment/${processId}/${commentId}`,
    data: comment,
    beforeSendingHook: beforeSendingComment,
  })
}

const deleteCommentToProcess = (axios: IFactoryFunctionRequest<any, any>) => (processId: string, commentId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/deleteComment/${processId}/${commentId}`,
  })
}

const addNoteToProcess = (axios: IFactoryFunctionRequest<any, any>) => (processId, note: any) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/addNote/${processId}`,
    data: note,
  })
}

const updateNoteToProcess = (axios: IFactoryFunctionRequest<any, any>) => (processId: string, noteId: string, headline: string, content: string) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/updateNote/${processId}/${noteId}`,
    data: { headline, content },
    beforeSendingHook: beforeSendingNote,
    afterReceivingHook: afterReceivingNoteResponse,
  })
}

const toggleNoteImportante = (axios: IFactoryFunctionRequest<any, any>) => (processId: string, noteId: string, isImportant: boolean) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/toggleNoteImportante/${processId}/${noteId}`,
    data: { isImportant },
  })
}

const toggleHideInPDF = (axios: IFactoryFunctionRequest<any, any>) => (processId: string, noteId: string, hideInPDF: boolean) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/toggleHideInPDF/${processId}/${noteId}`,
    data: { hideInPDF },
  })
}

const deleteNoteToProcess = (axios: IFactoryFunctionRequest<any, any>) => (processId: string, noteId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/deleteNote/${processId}/${noteId}`,
  })
}

const exportCandidatesToExel = (axios: IFactoryFunctionRequest<IResource, any >) => (query) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export`,
    query,
    immediate: false,
  })
}

const exportRecruitmentInterviewsToExcel = (axios: IFactoryFunctionRequest<IResource, any >) => (query) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export/interviews`,
    query,
    immediate: false,
  })
}

const getBase64CandidatePdf = (axios: IFactoryFunctionRequest<IResource, any >) => (processId, query) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/candidatePdf/${processId}`,
    query,
  })
}

const createInterviewProcess = (axios: IFactoryFunctionRequest<any, any >) => (datas) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${datas.processId}/rounds/${datas.roundIndex}`,
    data: datas,
  })
}

const attachMeetingToInterview = (axios: IFactoryFunctionRequest<any, any >) => ({ processId, roundIndex, interview, defaultRound }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${processId}/rounds/${roundIndex}/attachMeeting`,
    data: {
      interview,
      defaultRound,
    },
  })
}

const updateInterviewMeeting = (axios: IFactoryFunctionRequest<any, any >) => ({ processId, interview }) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/updateInterviewMeeting/${processId}/interview/${interview._id}`,
    data: { interview },
  })
}

const deleteInterview = (axios: IFactoryFunctionRequest<any, any >) => ({ processId, roundId, interviewId, keepMeeting }: { processId: string, roundId: string, interviewId: string, keepMeeting?: boolean }) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${processId}/rounds/${roundId}/interviews/${interviewId}`,
    query: keepMeeting ? { keepMeeting: true } : undefined,
  })
}

const fillCandidateEvaluation = (axios: IFactoryFunctionRequest<any, any >) => ({ closeEvaluation, processId, roundId, interviewId, recruiterId, recruiter, round }) => {
  return axios({
    method: EMethod.patch,
    query: { closeEvaluation },
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/fillEvaluation/${processId}/rounds/${roundId}/interviews/${interviewId}/recruiter/${recruiterId}`,
    data: { recruiter, round },
  })
}

const partialUpdateRound = (axios: IFactoryFunctionRequest<any, any >) => ({ processId, roundId, round }) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${processId}/rounds/${roundId}`,
    data: round,
  })
}

const partialUpdateRoundRecruiter = (axios: IFactoryFunctionRequest<any, any >) => ({ processId, roundId, interviewId, recruitmentId, values }) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${processId}/rounds/${roundId}/interview/${interviewId}/recruitment/${recruitmentId}`,
    data: values,
  })
}

const extractCandidateCV = (axios: IFactoryFunctionRequest) => (data, firmId): IFactoryResponse<any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/extractPdfDatas/${firmId}`,
    data,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

const candidateProcessOnboardingPage = (axios: IFactoryFunctionRequest<any, any>) => (): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/candidate/onboarding`,
    afterReceivingHook: afterReceiving
  })
}

const uploadCandidateFile = (axios: IFactoryFunctionRequest) => ({ processId, type, data }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/candidate/${processId}/file/${type}`,
    data,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}
const deleteCandidateFile = (axios: IFactoryFunctionRequest) => ({ processId, type }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/candidate/${processId}/file/${type}`,
  })
}

const retreiveMailHashTagsInfo = (axios: IFactoryFunctionRequest) => ({ processId }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/mailHashTags/${processId}`,
  })
}
const sendMailTemplateCandidate = (axios: IFactoryFunctionRequest) => (datas: { subject: string; html: string; processId: string; includeSignature: boolean; cc?: string; bcc?: string; attachments: { fileName: string; content: string; encoding: 'base64' }[] }[]): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/sendMailTemplateToCandidate`,
    data: datas,
  })
}

const showMyRecruitmentProcesses = (axios: IFactoryFunctionRequest) => ({ query }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/myRecruitmentProcesses`,
    query,
    afterReceivingHook: afterReceiving
  })
}

const showCandidateProcesses = (axios: IFactoryFunctionRequest) => ({ candidateId }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/candidate/${candidateId}/processes`,
    afterReceivingHook: afterReceiving
  })
}

const createCandidateApplication = (axios: IFactoryFunctionRequest) => (data): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/createCandidateApplication`,
    data,
    beforeSendingHook: beforeSendingCandidateAndProcess,
    afterReceivingHook: afterReceivingResource
  })
}

const recommendCandidate = (axios: IFactoryFunctionRequest) => (data): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recommendCandidate`,
    data,
    beforeSendingHook: beforeSendingCandidateAndProcess,
    afterReceivingHook: afterReceivingResource
  })
}

const recommendedCandidates = (axios: IFactoryFunctionRequest) => (): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recommendedCandidates`,
    afterReceivingHook: afterReceiving
  })
}

const calendarMeeting = (axios: IFactoryFunctionRequest) => (query): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/calendarMeeting`,
    query,
    afterReceivingHook: afterReceivingRecruitmentProcesses
  })
}
const cancelProcess = (axios: IFactoryFunctionRequest) => (process:{ _id: string, withdrawalCategory: any, chosenCompanyId:any }) => {
  // (processId): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/cancelCandidate/${process._id}`,
    data: process,
  })
}
const rejectProcess = (axios: IFactoryFunctionRequest) => (process:{ _id: string, rejectedCategory: any }) => {
  // (processId): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/rejectCandidate/${process._id}`,
    data: process,
  })
}
const cancelCancellationProcess = (axios: IFactoryFunctionRequest) => (processId): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/reactivateProcess/${processId}`,
  })
}

const createProcessFromResource = (axios: IFactoryFunctionRequest) => (resourceId): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/createProcessFromResource/${resourceId}`,
    afterReceivingHook: afterReceiving
  })
}


const globalSearch = (axios: IFactoryFunctionRequest<any, any>) => (searchString = '') => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/search`,
    query: { searchString },
    immediate: false,
  })
}

const sendEvaluationReminder = (axios: IFactoryFunctionRequest<any, any>) => (processId: string, resourceId, meeting) => {
    return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/sendEvaluationReminder/${processId}`,
    data: {resourceId, meeting}
  })
}

const sendCandidateFile = (axios: IFactoryFunctionRequest<any, any>) => (processId: string, interviewId: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/sendCandidateFile/${processId}/interview/${interviewId}`,
  })
}

const addNewRoundToProcess = (axios: IFactoryFunctionRequest<any, any>) => (processId: string, round: any) => {
    return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/addNewRoundToProcess/${processId}`,
    data: {round}
  })
}

const deleteRound = (axios: IFactoryFunctionRequest<any, any>) => (processId: string, roundId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${processId}/round/${roundId}`,
  })
}

const deleteProcess = (axios: IFactoryFunctionRequest<{ _id: string }, any>) => (processId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${processId}`,
  })
}

const countManagedByResource = (axios: IFactoryFunctionRequest<{ count: number }, any>) => (resourceId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/countManagedBy/resource/${resourceId}`,
  })
}

const replaceManagedByResource = (axios: IFactoryFunctionRequest<{ modifiedCount: number }, any>) => (resourceId: string, replacementResourceIds: string[] = []) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/replaceManagedBy/resource/${resourceId}`,
    data: { replacementResourceIds },
  })
}

export default (axios: AxiosInstance) => ({
  getByMeeting: getByMeeting(useAxios(axios)),
  screeningList: screeningList(useAxios(axios)),
  cancelProcess: cancelProcess(useAxios(axios)),
  rejectProcess: rejectProcess(useAxios(axios)),
  updateRecruitmentProcess: updateRecruitmentProcess(useAxios(axios)),
  partialUpdateRecruitmentProcess: partialUpdateRecruitmentProcess(useAxios(axios)),
  candidateBaseList: candidateBaseList(useAxios(axios)),
  getProcessList: getProcessList(useAxios(axios)),
  exportCandidatesToExel: exportCandidatesToExel(useAxios(axios)),
  exportRecruitmentInterviewsToExcel: exportRecruitmentInterviewsToExcel(useAxios(axios)),
  getProcessInterviewPage: getProcessInterviewPage(useAxios(axios)),
  getBase64CandidatePdf: getBase64CandidatePdf(useAxios(axios)),
  addDocumentToProcess: addDocumentToProcess(useAxios(axios)),
  deleteDocumentFromProcess: deleteDocumentFromProcess(useAxios(axios)),
  toggleDocumentPublicStatus: toggleDocumentPublicStatus(useAxios(axios)),
  moveDocumentInProcess: moveDocumentInProcess(useAxios(axios)),
  addCommentToProcess: addCommentToProcess(useAxios(axios)),
  updateCommentToProcess: updateCommentToProcess(useAxios(axios)),
  deleteCommentToProcess: deleteCommentToProcess(useAxios(axios)),
  addNoteToProcess: addNoteToProcess(useAxios(axios)),
  updateNoteToProcess: updateNoteToProcess(useAxios(axios)),
  toggleNoteImportante: toggleNoteImportante(useAxios(axios)),
  toggleHideInPDF: toggleHideInPDF(useAxios(axios)),
  deleteNoteToProcess: deleteNoteToProcess(useAxios(axios)),
  createInterviewProcess: createInterviewProcess(useAxios(axios)),
  updateInterviewMeeting: updateInterviewMeeting(useAxios(axios)),
  deleteInterview: deleteInterview(useAxios(axios)),
  getProcessEvaluation: getProcessEvaluation(useAxios(axios)),
  getProcessEditCandidateInformations: getProcessEditCandidateInformations(useAxios(axios)),
  fillCandidateEvaluation: fillCandidateEvaluation(useAxios(axios)),
  partialUpdateRound: partialUpdateRound(useAxios(axios)),
  partialUpdateRoundRecruiter: partialUpdateRoundRecruiter(useAxios(axios)),
  extractCandidateCV: extractCandidateCV(useAxios(axios)),
  getProcessCandidateInformation: getProcessCandidateInformation(useAxios(axios)),
  candidateProcessOnboardingPage: candidateProcessOnboardingPage(useAxios(axios)),
  uploadCandidateFile: uploadCandidateFile(useAxios(axios)),
  deleteCandidateFile: deleteCandidateFile(useAxios(axios)),
  retreiveMailHashTagsInfo: retreiveMailHashTagsInfo(useAxios(axios)),
  sendMailTemplateCandidate: sendMailTemplateCandidate(useAxios(axios)),
  showMyRecruitmentProcesses: showMyRecruitmentProcesses(useAxios(axios)),
  createCandidateApplication: createCandidateApplication(useAxios(axios)),
  attachMeetingToInterview: attachMeetingToInterview(useAxios(axios)),
  showCandidateProcesses: showCandidateProcesses(useAxios(axios)),
  calendarMeeting: calendarMeeting(useAxios(axios)),
  recommendCandidate: recommendCandidate(useAxios(axios)),
  recommendedCandidates: recommendedCandidates(useAxios(axios)),
  cancelCancellationProcess: cancelCancellationProcess(useAxios(axios)),
  createProcessFromResource: createProcessFromResource(useAxios(axios)),
  globalSearch: globalSearch(useAxios(axios)),
  updateManyStatuses: updateManyStatuses(useAxios(axios)),
  sendEvaluationReminder: sendEvaluationReminder(useAxios(axios)),
  sendCandidateFile: sendCandidateFile(useAxios(axios)),
  addNewRoundToProcess: addNewRoundToProcess(useAxios(axios)),
  deleteRound: deleteRound(useAxios(axios)),
  deleteProcess: deleteProcess(useAxios(axios)),
  countManagedByResource: countManagedByResource(useAxios(axios)),
  replaceManagedByResource: replaceManagedByResource(useAxios(axios)),
})
